/**
 * 心愿单 Wishlist
 *
 * Saved products live in localStorage only — there is no account sync, so a
 * list does not follow the shopper to another device or browser. Storage
 * reuses webvista.storeData/retrieveData (global.js), the same helper the
 * recently-viewed feature uses, and holds a plain array of product ids in the
 * order they were saved.
 *
 * Every toggle also publishes a storefront custom event
 * (wishlist_added / wishlist_removed) via Shopify.analytics.publish, so a
 * Shopify custom pixel can forward it to Meta, GA4 or Klaviyo later without
 * any change to the theme.
 *
 * Elements:
 *   <wishlist-button>   heart toggle, rendered by snippets/wishlist-button.liquid
 *   <wishlist-count>    saved-item count, hidden while the list is empty
 *   <wishlist-products> the saved-items grid on the wishlist page
 */
if (!window.themeWishlist) {
  const BASE_KEY = "wishlist";
  const EVENT = "wishlist:change";

  // Storage is namespaced per signed-in customer (data-customer-id on <html>,
  // set in theme.liquid) so a shared device does not leak one person's saves
  // to the next. Logged out, the key stays the original bare "wishlist".
  const KEY = (function () {
    const id = document.documentElement.getAttribute("data-customer-id");
    return id ? BASE_KEY + "_" + id : BASE_KEY;
  })();
  // Caps both the stored list and the length of the search URL built from it.
  // Oldest entries fall off the front once the cap is reached.
  const LIMIT = 50;

  window.themeWishlist = {
    EVENT: EVENT,
    LIMIT: LIMIT,

    /**
     * 读取收藏的产品ID
     * @returns {number[]} 按收藏顺序排列的产品ID
     */
    read: function () {
      if (typeof webvista === "undefined") return [];
      const stored = webvista.retrieveData(KEY);
      if (!Array.isArray(stored)) return [];
      return stored.map(Number).filter((id) => Number.isFinite(id) && id > 0);
    },

    /**
     * 判断产品是否已收藏
     * @param {number|string} id 产品ID
     * @returns {boolean}
     */
    has: function (id) {
      return this.read().indexOf(Number(id)) >= 0;
    },

    /**
     * 收藏数量
     * @returns {number}
     */
    count: function () {
      return this.read().length;
    },

    /**
     * 切换收藏状态
     * @param {number|string} id 产品ID
     * @param {Object} [meta={}] 用于埋点的产品信息
     * @returns {boolean} 切换后是否为已收藏
     */
    toggle: function (id, meta = {}) {
      id = Number(id);
      if (!Number.isFinite(id) || id <= 0) return false;

      const ids = this.read();
      const index = ids.indexOf(id);
      const added = index < 0;

      if (added) {
        ids.push(id);
        while (ids.length > LIMIT) ids.shift();
      } else {
        ids.splice(index, 1);
      }

      if (typeof webvista !== "undefined") webvista.storeData(KEY, ids);

      // meta rides along so snippets/wishlist-ads.liquid can build an ad
      // event without re-reading the DOM.
      document.dispatchEvent(
        new CustomEvent(EVENT, { detail: { id, added, ids, meta } }),
      );
      this.publish(added ? "wishlist_added" : "wishlist_removed", id, meta);

      return added;
    },

    /**
     * 发送自定义事件给 Shopify custom pixel
     * @param {string} name 事件名
     * @param {number} id 产品ID
     * @param {Object} meta 产品信息
     */
    publish: function (name, id, meta) {
      try {
        if (typeof Shopify === "undefined") return;
        if (!Shopify.analytics || !Shopify.analytics.publish) return;

        const price = Number(meta.price);
        Shopify.analytics.publish(name, {
          productId: String(id),
          // The pixel joins these into the ad platform's catalog id; the
          // format differs per catalog so it is not assembled here. market is
          // the visitor's country, which decides the catalog id on a store
          // selling into more than one market.
          variantId: meta.variantId || "",
          market: meta.market || "",
          handle: meta.handle || "",
          title: meta.title || "",
          value: Number.isFinite(price) ? price / 100 : 0,
          currency: meta.currency || "",
          wishlistCount: this.count(),
        });
      } catch (e) {
        // 埋点失败不影响收藏功能
      }
    },
  };
}

if (!customElements.get("wishlist-button")) {
  customElements.define(
    "wishlist-button",
    class WishlistButton extends HTMLElement {
      constructor() {
        super();

        this.toggle = this.querySelector(".wishlist-button__toggle");
        this.label = this.querySelector(".wishlist-button__label");
        this.onStoreChange = this.render.bind(this);

        if (this.toggle) {
          this.toggle.addEventListener("click", this.onClick.bind(this));
        }
      }

      connectedCallback() {
        document.addEventListener(window.themeWishlist.EVENT, this.onStoreChange);
        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener(
          window.themeWishlist.EVENT,
          this.onStoreChange,
        );
      }

      get productId() {
        return this.dataset.productId;
      }

      onClick(event) {
        event.preventDefault();
        event.stopPropagation();

        // Gated buttons never reach storage: send the shopper to sign in and
        // let them come back to this page to save.
        if (this.dataset.gated === "true") {
          if (this.dataset.loginUrl) window.location.href = this.dataset.loginUrl;
          return;
        }

        const added = window.themeWishlist.toggle(this.productId, {
          variantId: this.dataset.variantId,
          market: this.dataset.market,
          handle: this.dataset.productHandle,
          title: this.dataset.productTitle,
          price: this.dataset.productPrice,
          currency: this.dataset.currency,
        });

        if (added && this.toggle) {
          this.toggle.classList.remove("wishlist-button--pop");
          // 强制重排，保证连续点击时动画能重新播放
          void this.toggle.offsetWidth;
          this.toggle.classList.add("wishlist-button--pop");
        }
      }

      render() {
        if (!this.toggle) return;

        // A gated button has no saved state to reflect — it only ever invites
        // the shopper to sign in, so don't let the store's state relabel it.
        if (this.dataset.gated === "true") {
          const prompt = this.dataset.textSignIn;
          this.toggle.setAttribute("aria-pressed", "false");
          if (prompt) {
            this.toggle.setAttribute("aria-label", prompt);
            if (this.label) this.label.textContent = prompt;
          }
          return;
        }

        const saved = window.themeWishlist.has(this.productId);
        const text = saved ? this.dataset.textSaved : this.dataset.textSave;

        this.toggle.setAttribute("aria-pressed", saved ? "true" : "false");
        if (text) this.toggle.setAttribute("aria-label", text);
        if (this.label && text) this.label.textContent = text;
      }
    },
  );
}

if (!customElements.get("wishlist-count")) {
  customElements.define(
    "wishlist-count",
    class WishlistCount extends HTMLElement {
      constructor() {
        super();
        this.onStoreChange = this.render.bind(this);
      }

      connectedCallback() {
        document.addEventListener(window.themeWishlist.EVENT, this.onStoreChange);
        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener(
          window.themeWishlist.EVENT,
          this.onStoreChange,
        );
      }

      render() {
        const count = window.themeWishlist.count();
        this.textContent = count > 99 ? "99+" : String(count);
        this.hidden = count === 0;
      }
    },
  );
}

if (!customElements.get("wishlist-products")) {
  customElements.define(
    "wishlist-products",
    class WishlistProducts extends HTMLElement {
      constructor() {
        super();

        this.container = this.querySelector(".products-container");
        this.empty = this.querySelector(".wishlist-empty");
        this.placeholder = this.querySelector("template[data-wishlist-placeholder]");
        this.productIds = [];
        this.onStoreChange = this.onWishlistChange.bind(this);
      }

      connectedCallback() {
        document.addEventListener(window.themeWishlist.EVENT, this.onStoreChange);

        const url = this.constructQueryUrl();
        if (!url) return this.showEmpty();

        this.showPlaceholder();
        this.fetchData(url);
      }

      disconnectedCallback() {
        document.removeEventListener(
          window.themeWishlist.EVENT,
          this.onStoreChange,
        );
      }

      /**
       * 取消收藏时就地移除卡片，避免整页重新请求
       * @param {CustomEvent} event
       */
      onWishlistChange(event) {
        if (!this.container || event.detail.added) return;

        const item = this.container.querySelector(
          `li[data-product-id="${event.detail.id}"]`,
        );
        if (item) item.remove();

        if (!this.container.querySelector("li[data-product-id]")) {
          this.container.innerHTML = "";
          this.showEmpty();
        }
      }

      showPlaceholder() {
        if (!this.container || !this.placeholder) return;
        this.container.innerHTML = "";
        this.container.append(this.placeholder.content.cloneNode(true));
      }

      showEmpty() {
        if (this.container) this.container.hidden = true;
        if (this.empty) this.empty.hidden = false;
      }

      fetchData(url) {
        webvista
          .fetchHtml(url)
          .then((html) => this.renderHtml(html))
          .catch(() => this.showEmpty());
      }

      renderHtml(html) {
        const results = html.querySelector("template[data-wishlist-results]");
        if (!results || !this.container) return this.showEmpty();

        const source = results.content;
        const itemListWrapper = source.querySelector(".slider-wrapper");
        if (!itemListWrapper) return this.showEmpty();

        // 按收藏顺序（最新在前）重排搜索结果
        const fragment = document.createDocumentFragment();
        this.productIds.forEach((id) => {
          const item = itemListWrapper.querySelector(
            `li[data-product-id="${id}"]`,
          );
          if (item) fragment.appendChild(item);
        });

        if (!fragment.childElementCount) return this.showEmpty();

        itemListWrapper.innerHTML = "";
        itemListWrapper.appendChild(fragment);

        this.container.innerHTML = "";
        this.container.append(source);
        this.container.hidden = false;
        if (this.empty) this.empty.hidden = true;

        webvista.initLazyImages();
        webvista.initTooltips();
      }

      constructQueryUrl() {
        // 最新收藏排在最前
        this.productIds = window.themeWishlist
          .read()
          .slice(-window.themeWishlist.LIMIT)
          .reverse();

        if (this.productIds.length <= 0) return null;

        const baseUrl = this.dataset.searchUrl;
        const query = this.productIds.map((id) => `id:${id}`).join(" OR ");
        const params = new URLSearchParams({
          q: query,
          type: "product",
          section_id: this.dataset.section,
          ose: "false",
        });
        return `${baseUrl}?${params.toString()}`;
      }
    },
  );
}
